import { SingleSelect } from '../../../../../widgets/FormikInputFieldV2';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onboardingSelector } from '../../../../../reducers/onboarding';
import { sessionSelector } from '../../../../../reducers/session';
import { OptionType } from '../../../../../types';
import { apiRequest } from '../../../../../APIClient';
import { useFormikContext } from 'formik';
import { getSeriesLabel, SensorFormValues } from '../../v2/sensor/constants';
import WarningAlert from '../../../../../widgets/WarningAlert';
import SensorCapabilityForm from './SensorCapabilityForm';
import { memoizedHardwareSelectors } from '../../../../../selectors';

type FormikTypes = Pick<
  SensorFormValues,
  'series' | 'recommendationOverridden'
>;

const GenericSensorSelect: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const allGenericSensors = useSelector(
    onboardingSelector.getAvailableGenericSensors,
  );
  const remainingSeries = useSelector(
    memoizedHardwareSelectors.getRemainingGenericSeries,
  );

  const sensorCapabilities = useSelector(
    onboardingSelector.getSensorCapabilities,
  );

  const { values } = useFormikContext<FormikTypes>();
  const capabilitySchema = sensorCapabilities[values.series];

  React.useEffect(() => {
    if (values.series && !sensorCapabilities[values.series]) {
      dispatch(apiRequest.getSensorCapabilities(values.series));
    }
  }, [values.series, dispatch, sensorCapabilities]);

  const isAwnUser = useSelector(sessionSelector.isAwnUser);

  const purchasedOptions = React.useMemo<OptionType[]>(
    () =>
      Object.keys(remainingSeries)
        .filter((key) => remainingSeries[key] > 0) // Only display available series
        .map((key) => ({
          value: key,
          label: getSeriesLabel(allGenericSensors, key ?? ''),
        })),
    [remainingSeries, allGenericSensors],
  );

  return (
    <>
      <SingleSelect
        name="series"
        id="selectSeries"
        placeholder={'Choose a model series'}
        options={isAwnUser ? allGenericSensors : purchasedOptions}
        className="mb-3"
        label="Sensor Series"
        isDisabled={values.recommendationOverridden && !isAwnUser}
        required
      />
      {values.recommendationOverridden && (
        <WarningAlert>
          The sensor model has been confirmed by the Onboarding team
        </WarningAlert>
      )}
      {capabilitySchema && capabilitySchema.length > 0 && (
        <SensorCapabilityForm capabilitySchema={capabilitySchema} />
      )}
    </>
  );
};

export default GenericSensorSelect;
